<template>
  <main
    v-style="{
      bgv: { sm: `none`, md: `#ffffff url(${backgroundImage.src.md}) no-repeat top center/cover` },
    }"
  >
    <section
      v-style="{
        bgv: { sm: `#ffffff url(${backgroundImage.src.sm}) no-repeat center/cover`, md: `none` },
        h: { sm: '50dvh' },
      }"
      class="px-4 pt-18 text-center space-y-2 md:mb-18 md:h-a"
    >
      <h1 class="display-2">
        {{ heading }}
      </h1>
      <p v-for="(description, key) in descriptions" :key class="text-sm lh-4 ws-pre-wrap md:text-base">
        {{ description }}
      </p>
    </section>
    <section v-style:p="{ md: '0 0 6.5rem 0' }" class="f-col">
      <div v-if="formState === FormState.INITIAL" class="mx-a px-6 py-10 bg-white f-col max-w-2xl md:px-19">
        <article class="lh-5 mb-4 text-center space-y-4">
          <h2 class="title-1">
            {{ formSection.title }}
          </h2>

          <p>
            {{ formSection.description }}
          </p>
        </article>

        <form class="space-y-4" @submit.prevent="onFormSubmit">
          <vf-input v-model.trim="productName" :placeholder="formSection.modelName">
            {{ formSection.searchName }}
          </vf-input>

          <vf-input v-model.trim="productSku" :placeholder="formSection.skuCodeName">
            {{ formSection.searchCode }}

            <template #end>
              <vf-toggletip placement="top-end">
                <template #trigger>
                  <span class="text-xs">
                    <vf-icon name="info" />
                  </span>
                </template>

                {{ formSection.tooltipSearchCode }}
              </vf-toggletip>
            </template>
          </vf-input>

          <div v-if="errorMessage" class="text-xs c-red-30 text-center">
            {{ errorMessage }}
          </div>

          <vf-button class="mx-a flex" type="submit">
            {{ formSection.confirm }}
          </vf-button>
        </form>
      </div>

      <div v-if="formState === FormState.RESULT" class="mx-a mb-12 w-full md:mb-0 md:w-2xl">
        <div class="c-white fw-medium py-6 text-center bg-black w-full">
          {{ filteredAssets.length }} {{ $pluralize(resultSection.resultsFound, filteredAssets.length) }}
        </div>

        <div v-style:max-h="{ md: '28rem' }" class="overflow-y-auto">
          <vf-table class="w-full">
            <!-- added an empty header as this table doesn't need one -->
            <template #head>
              <th class="hidden">
                header
              </th>
            </template>

            <tr
              v-for="({ name, sku, url }, key) in filteredAssets"
              :key
              class="text-sm text-left bg-white"
              :class="{ 'bg-grey-80': key % 2 }"
            >
              <td class="py-6 pl-6">
                {{ sku }}
              </td>
              <td class="py-6 pl-2">
                {{ name }}
              </td>
              <td class="pr-4 text-right">
                <base-link class="px-6 py-2 b" :to="url">
                  <span class="<md:hidden">{{ resultSection.view }}</span>
                  <span class="md:hidden">{{ resultSection.pdf }}</span>
                </base-link>
              </td>
            </tr>
          </vf-table>
        </div>

        <vf-button class="mx-a mt-6 px-14 f-col" @click="reset">
          <vf-icon name="refresh" size="md" />
          <span>{{ resultSection.reset }}</span>
        </vf-button>
      </div>
    </section>
  </main>
</template>

<script setup lang="ts">
import type { CloudinaryAsset, DeclarationOfConformityAsset } from '#types/cloudinaryAsset'

const cloudinaryUrlPrefix = 'https://assets.thenorthface.eu'

const { data: assets } = await useFetch(`${cloudinaryUrlPrefix}/any/list/declaration-of-conformity.json`, {
  transform: ({ resources }: { resources: CloudinaryAsset[] }): DeclarationOfConformityAsset[] => {
    return resources.map(({ format, public_id, resource_type, type, version }) => {
      /**
       * filename pattern
       * DOC_SKU_SEASON_DATECODE_NAME_...
       *
       * @example
       * DOC_CGV7_SP21_XXXX_DIPSEA-COVER-IT
       * DOC_NF0A2UOP_SP21_XXXX_W-APHRODITE-2.0-PANT
       * DOC_NF0A8BBM_SP25_XXXX_M-SUNRISER-1:4-ZIP
       *
       * Some documents require a / in the product name.
       * Cloudinary transforms these to :
       *
       * @example
       * TEEN SUNRISER S/S TEE
       * M SUNRISER 1/4 ZIP
       */
      const parts = public_id
        .replace(/-/g, ' ')
        .replace(/:/g, '/')
        .split('_')

      const [prefix, sku, , datecode, name] = parts

      const url = `${cloudinaryUrlPrefix}/${resource_type}/${type}/v${version}/${public_id}.${format}`

      return {
        datecode,
        name,
        prefix,
        sku,
        url
      }
    })
  }
})

if (!assets.value) throw createError({ fatal: true })

const { data, error } = await useApi().internal.getStaticPage('help-section/terms-and-conditions/declaration-of-conformity')
if (!data.value) throw createError({ ...error.value!, fatal: true })

const { heading, descriptions, backgroundImage, formSection, resultSection } = data.value
const enum FormState {
  INITIAL,
  RESULT,
}

const filteredAssets = ref<DeclarationOfConformityAsset[]>([])
const formState = ref<FormState>(FormState.INITIAL)
const errorMessage = ref('')
const productSku = ref('')
const productName = ref('')

const onFormSubmit = () => {
  errorMessage.value = ''

  if (!productSku.value && !productName.value) {
    errorMessage.value = formSection.errorMessageProductCodeOrNameMissing
    return
  }

  if (assets.value === null) assets.value = []

  const results = assets.value.filter(({ name, prefix, sku }) => {
    if (prefix.toLowerCase() !== 'doc') return false

    return (!productSku.value || sku.toLowerCase().startsWith(productSku.value.toLowerCase()))
      && (!productName.value || name.toLowerCase().includes(productName.value.toLowerCase()))
  })

  if (!results.length) {
    errorMessage.value = formSection.errorMessageInputNotValid
    return
  }

  formState.value = FormState.RESULT
  filteredAssets.value = results
}

const reset = () => {
  filteredAssets.value = []
  formState.value = FormState.INITIAL
  productSku.value = ''
  productName.value = ''
}
</script>
